import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { $ } from '../utils/dom';
import { formatDate } from '../utils/admin-labels';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface AttendanceRank {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  attendanceCount: number;
}

interface Exercise {
  id: string;
  name: string;
}

interface PRRank {
  id: string;
  weight: number;
  reps: number;
  date: string;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export class AdminRankingsPage implements View {
  private activeTab: 'attendance' | 'prs' = 'attendance';
  private attendanceRanking: AttendanceRank[] = [];
  private exercises: Exercise[] = [];
  private selectedExerciseId = '';
  private prRanking: PRRank[] = [];
  private loading = true;
  private prLoading = false;

  async render(): Promise<string> {
    const params = router.getParams();
    if (params.tab === 'prs') this.activeTab = 'prs';
    if (params.exerciseId) this.selectedExerciseId = params.exerciseId;

    if (this.loading) {
      await this.loadInitial();
    }

    if (this.selectedExerciseId && this.prRanking.length === 0 && !this.prLoading) {
      await this.loadPRRanking();
    }

    const breadcrumbHtml = `<nav class="admin-breadcrumb">
      <a href="#/admin/students">Alumnos</a>
      <span class="admin-breadcrumb-sep">›</span>
      <span>Rankings</span>
    </nav>`;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          ${breadcrumbHtml}
          <h2 data-testid="admin-rankings-title" style="display:flex;align-items:center;gap:0.6rem;">${getTyrisIcon('rankings', { size: 26 })} Rankings del Gimnasio</h2>
          <p>Asistencia y récords personales de todos los alumnos.</p>
        </div>
      </div>

      <div style="display:flex;gap:1rem;margin-bottom:1.5rem;border-bottom:1px solid var(--border-metallic);padding-bottom:1rem;">
        <button id="tab-attendance" class="btn ${this.activeTab === 'attendance' ? 'btn-primary' : 'btn-outline'}" data-testid="tab-attendance" style="display:inline-flex;align-items:center;gap:0.4rem;">
          ${getTyrisIcon('asistencia', { size: 16 })} Asistencia
        </button>
        <button id="tab-prs" class="btn ${this.activeTab === 'prs' ? 'btn-primary' : 'btn-outline'}" data-testid="tab-prs" style="display:inline-flex;align-items:center;gap:0.4rem;">
          ${getTyrisIcon('mis-records', { size: 16 })} PRs por ejercicio
        </button>
      </div>

      <div id="tab-content">
        ${this.activeTab === 'attendance' ? this.renderAttendanceTab() : this.renderPRsTab()}
      </div>
    `;
  }

  private renderAttendanceTab(): string {
    return `
      <section class="card-glass admin-table-wrap" data-testid="admin-rankings-attendance">
        <h3 class="admin-section-title">Ranking de asistencia</h3>
        ${
          this.attendanceRanking.length === 0
            ? `<div class="admin-empty"><p>Sin datos de asistencia.</p></div>`
            : `
          <table class="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Alumno</th>
                <th>Email</th>
                <th>Asistencias</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${this.attendanceRanking
                .map(
                  (r, i) => `
                <tr>
                  <td><strong>${i + 1}</strong>${i === 0 ? ' <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:#E5C158;color:#0F1218;font-size:0.7rem;font-weight:800;">1</span>' : i === 1 ? ' <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:#A8A8A8;color:#0F1218;font-size:0.7rem;font-weight:800;">2</span>' : i === 2 ? ' <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:#C08A54;color:#0F1218;font-size:0.7rem;font-weight:800;">3</span>' : ''}</td>
                  <td data-testid="ranking-student-name">${r.firstName} ${r.lastName}</td>
                  <td class="admin-cell-muted">${r.email}</td>
                  <td><strong>${r.attendanceCount}</strong></td>
                  <td>
                    <a href="#/admin/student?id=${r.id}" class="btn btn-outline btn-sm">Ver ficha</a>
                    <a href="#/admin/attendance?studentId=${r.id}" class="btn btn-outline btn-sm">Asistencia</a>
                  </td>
                </tr>
              `,
                )
                .join('')}
            </tbody>
          </table>
        `
        }
      </section>
    `;
  }

  private renderPRsTab(): string {
    return `
      <section class="card-glass" style="margin-bottom:1.5rem;" data-testid="admin-rankings-pr-selector">
        <h3 class="admin-section-title">Seleccionar ejercicio</h3>
        <div style="display:flex;gap:1rem;align-items:center;flex-wrap:wrap;">
          <select id="exercise-select" class="admin-select" data-testid="pr-exercise-select" style="min-width:260px;">
            <option value="">— Selecciona un ejercicio —</option>
            ${this.exercises
              .map(
                (e) =>
                  `<option value="${e.id}" ${e.id === this.selectedExerciseId ? 'selected' : ''}>${e.name}</option>`,
              )
              .join('')}
          </select>
        </div>
      </section>

      ${
        !this.selectedExerciseId
          ? `<div class="admin-empty" data-testid="admin-rankings-no-exercise"><p>Selecciona un ejercicio para ver el ranking de PRs.</p></div>`
          : this.prLoading
            ? `<div class="admin-state-loading"><p>Cargando ranking...</p></div>`
            : `
          <section class="card-glass admin-table-wrap" data-testid="admin-rankings-prs">
            <h3 class="admin-section-title">Ranking de PRs — ${this.exercises.find((e) => e.id === this.selectedExerciseId)?.name ?? ''}</h3>
            ${
              this.prRanking.length === 0
                ? `<div class="admin-empty"><p>Sin récords registrados para este ejercicio.</p></div>`
                : `
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Alumno</th>
                    <th>Peso máx.</th>
                    <th>Reps</th>
                    <th>Fecha</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  ${this.prRanking
                    .map(
                      (r, i) => `
                    <tr>
                      <td><strong>${i + 1}</strong>${i === 0 ? ' <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:#E5C158;color:#0F1218;font-size:0.7rem;font-weight:800;">1</span>' : i === 1 ? ' <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:#A8A8A8;color:#0F1218;font-size:0.7rem;font-weight:800;">2</span>' : i === 2 ? ' <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:#C08A54;color:#0F1218;font-size:0.7rem;font-weight:800;">3</span>' : ''}</td>
                      <td data-testid="pr-student-name">${r.student.firstName} ${r.student.lastName}</td>
                      <td><strong>${r.weight} kg</strong></td>
                      <td>${r.reps}</td>
                      <td class="admin-cell-muted">${formatDate(r.date)}</td>
                      <td>
                        <a href="#/admin/student?id=${r.student.id}" class="btn btn-outline btn-sm">Ver ficha</a>
                      </td>
                    </tr>
                  `,
                    )
                    .join('')}
                </tbody>
              </table>
            `
            }
          </section>
        `
      }
    `;
  }

  afterRender(): void {
    $('#tab-attendance')?.addEventListener('click', () => {
      window.location.hash = '#/admin/rankings';
    });

    $('#tab-prs')?.addEventListener('click', () => {
      window.location.hash = '#/admin/rankings?tab=prs';
    });

    const exerciseSelect = $('#exercise-select') as HTMLSelectElement | null;
    exerciseSelect?.addEventListener('change', () => {
      if (exerciseSelect.value) {
        window.location.hash = `#/admin/rankings?tab=prs&exerciseId=${exerciseSelect.value}`;
      } else {
        window.location.hash = '#/admin/rankings?tab=prs';
      }
    });
  }

  private async loadInitial(): Promise<void> {
    try {
      const [ranking, exercises] = await Promise.all([
        apiClient.get<any>('/api/attendance/ranking'),
        apiClient.get<Exercise[]>('/api/exercises'),
      ]);
      this.attendanceRanking = Array.isArray(ranking) ? ranking : (ranking?.data ?? []);
      this.exercises = exercises;
    } catch {
      this.attendanceRanking = [];
      this.exercises = [];
    } finally {
      this.loading = false;
    }
  }

  private async loadPRRanking(): Promise<void> {
    try {
      const response = await apiClient.get<any>(
        `/api/records/ranking/${this.selectedExerciseId}`,
      );
      this.prRanking = Array.isArray(response) ? response : (response?.data ?? []);
    } catch {
      this.prRanking = [];
    }
  }
}
